import { fetchWeatherApi } from 'openmeteo';

export interface CurrentWeatherResponse {
  latitude: number;
  longitude: number;
  elevation: number;
  timezone: string;
  timezoneAbbreviation: string;
  current: {
    time: string;
    temperature_2m: number;
    weather_code: number;
    wind_speed_10m: number;
    wind_direction_10m: number;
  };
}

export interface WeatherData {
  location: string;
  temperature: number;
  windSpeed: number;
  windDirection: number;
  weatherCode: number;
  rawResponse: CurrentWeatherResponse;
}

export interface DailyForecastData {
  date: string;
  temperatureMax: number;
  temperatureMin: number;
  precipitationSum: number;
  windSpeedMax: number;
  weatherCode: number;
}

export interface DailyForecastResponse {
  latitude: number;
  longitude: number;
  elevation: number;
  timezone: string;
  timezoneAbbreviation: string;
  daily: {
    time: string[];
    temperature_2m_max: number[];
    temperature_2m_min: number[];
    precipitation_sum: number[];
    wind_speed_10m_max: number[];
    weather_code: number[];
  };
}

export interface ForecastData {
  location: string;
  daily: DailyForecastData[];
  rawResponse: DailyForecastResponse;
}

export class WeatherService {
  
  isRaining(weatherCode: number): boolean {
    // Weather codes for rain conditions (based on WMO codes)
    const rainCodes = [
      51, 53, 55, // Drizzle
      56, 57,     // Freezing drizzle
      61, 63, 65, // Rain
      66, 67,     // Freezing rain
      80, 81, 82, // Rain showers
      95, 96, 97  // Thunderstorm
    ];
    return rainCodes.includes(weatherCode);
  }

  getWeatherDescription(weatherCode: number): string {
    // WMO weather codes mapping to human-readable descriptions
    const weatherDescriptions: { [key: number]: string } = {
      0: 'Clear sky',
      1: 'Mainly clear',
      2: 'Partly cloudy',
      3: 'Overcast',
      45: 'Fog',
      48: 'Depositing rime fog',
      51: 'Light drizzle',
      53: 'Moderate drizzle',
      55: 'Dense drizzle',
      56: 'Light freezing drizzle',
      57: 'Dense freezing drizzle',
      61: 'Slight rain',
      63: 'Moderate rain',
      65: 'Heavy rain',
      66: 'Light freezing rain',
      67: 'Heavy freezing rain',
      71: 'Slight snow fall',
      73: 'Moderate snow fall',
      75: 'Heavy snow fall',
      77: 'Snow grains',
      80: 'Slight rain showers',
      81: 'Moderate rain showers',
      82: 'Violent rain showers',
      85: 'Slight snow showers',
      86: 'Heavy snow showers',
      95: 'Thunderstorm',
      96: 'Thunderstorm with slight hail',
      99: 'Thunderstorm with heavy hail'
    };

    return weatherDescriptions[weatherCode] || `Unknown weather condition (${weatherCode})`;
  }

  async getWeatherForLocation(latitude: number, longitude: number, locationName?: string): Promise<WeatherData> {
    const params = {
      latitude: [latitude],
      longitude: [longitude],
      current: 'temperature_2m,weather_code,wind_speed_10m,wind_direction_10m',
      timezone: 'auto'
    };

    const url = 'https://api.open-meteo.com/v1/forecast';
    
    try {
      const responses = await fetchWeatherApi(url, params);
      const response = responses[0];
      
      // Access current weather data
      const current = response.current()!;
      
      const weatherData: WeatherData = {
        location: locationName || `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`,
        temperature: current.variables(0)!.value(), // temperature_2m
        windSpeed: current.variables(2)!.value(), // wind_speed_10m
        windDirection: current.variables(3)!.value(), // wind_direction_10m
        weatherCode: current.variables(1)!.value(), // weather_code
        rawResponse: {
          latitude: response.latitude(),
          longitude: response.longitude(),
          elevation: response.elevation(),
          timezone: response.timezone(),
          timezoneAbbreviation: response.timezoneAbbreviation(),
          current: {
            time: new Date(Number(current.time()) * 1000).toISOString(),
            temperature_2m: current.variables(0)!.value(),
            weather_code: current.variables(1)!.value(),
            wind_speed_10m: current.variables(2)!.value(),
            wind_direction_10m: current.variables(3)!.value(),
          }
        }
      };
      
      return weatherData;
    } catch (error) {
      console.error('Error fetching weather data:', error);
      throw new Error(`Failed to fetch weather data: ${error.message}`);
    }
  }

  async get7DayForecast(latitude: number, longitude: number, locationName?: string): Promise<ForecastData> {
    const params = {
      latitude: [latitude],
      longitude: [longitude],
      daily: 'temperature_2m_max,temperature_2m_min,precipitation_sum,wind_speed_10m_max,weather_code',
      timezone: 'auto',
      forecast_days: 7
    };

    const url = 'https://api.open-meteo.com/v1/forecast';
    
    try {
      const responses = await fetchWeatherApi(url, params);
      const response = responses[0];
      
      // Access daily forecast data
      const daily = response.daily()!;
      const dailyData: DailyForecastData[] = [];
      
      // Get the data arrays
      const tempMaxArray: number[] = Array.from(daily.variables(0)!.valuesArray()!); // temperature_2m_max
      const tempMinArray: number[] = Array.from(daily.variables(1)!.valuesArray()!); // temperature_2m_min
      const precipitationArray: number[] = Array.from(daily.variables(2)!.valuesArray()!); // precipitation_sum
      const windSpeedArray: number[] = Array.from(daily.variables(3)!.valuesArray()!); // wind_speed_10m_max
      const weatherCodeArray: number[] = Array.from(daily.variables(4)!.valuesArray()!); // weather_code
      
      // Process each day's data (limited to 7 days)
      const numDays = Math.min(7, tempMaxArray.length);
      for (let i = 0; i < numDays; i++) {
        // Convert Unix timestamp to ISO date string
        const timeValue = daily.timeEnd() ? Number(daily.timeEnd()) + (i * 24 * 60 * 60) : Number(daily.time()) + (i * 24 * 60 * 60);
        const date = new Date(timeValue * 1000).toISOString().split('T')[0];
        
        dailyData.push({
          date,
          temperatureMax: tempMaxArray[i],
          temperatureMin: tempMinArray[i],
          precipitationSum: precipitationArray[i],
          windSpeedMax: windSpeedArray[i],
          weatherCode: weatherCodeArray[i],
        });
      }
      
      // Build raw response with proper time array conversion
      const timeStrings = Array.from({length: numDays}, (_, i) => {
        const timeValue = daily.timeEnd() ? Number(daily.timeEnd()) + (i * 24 * 60 * 60) : Number(daily.time()) + (i * 24 * 60 * 60);
        return new Date(timeValue * 1000).toISOString().split('T')[0];
      });
      
      const forecastData: ForecastData = {
        location: locationName || `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`,
        daily: dailyData,
        rawResponse: {
          latitude: response.latitude(),
          longitude: response.longitude(),
          elevation: response.elevation(),
          timezone: response.timezone(),
          timezoneAbbreviation: response.timezoneAbbreviation(),
          daily: {
            time: timeStrings,
            temperature_2m_max: tempMaxArray.slice(0, numDays),
            temperature_2m_min: tempMinArray.slice(0, numDays),
            precipitation_sum: precipitationArray.slice(0, numDays),
            wind_speed_10m_max: windSpeedArray.slice(0, numDays),
            weather_code: weatherCodeArray.slice(0, numDays),
          }
        }
      };
      
      return forecastData;
    } catch (error) {
      console.error('Error fetching forecast data:', error);
      throw new Error(`Failed to fetch forecast data: ${error.message}`);
    }
  }

  getCurrentLocationWeather(): Promise<WeatherData> {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by this browser.'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            const { latitude, longitude } = position.coords;
            const weatherData = await this.getWeatherForLocation(
              latitude,
              longitude,
              'Current Location'
            );
            resolve(weatherData);
          } catch (error) {
            reject(error);
          }
        },
        (error) => {
          let errorMessage = 'Unable to retrieve your location.';
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Location access denied by user.';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Location information is unavailable.';
              break;
            case error.TIMEOUT:
              errorMessage = 'Location request timed out.';
              break;
          }
          reject(new Error(errorMessage));
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000 // 5 minutes
        }
      );
    });
  }

  getCurrentLocationForecast(): Promise<ForecastData> {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by this browser.'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            const { latitude, longitude } = position.coords;
            const forecastData = await this.get7DayForecast(
              latitude,
              longitude,
              'Current Location'
            );
            resolve(forecastData);
          } catch (error) {
            reject(error);
          }
        },
        (error) => {
          let errorMessage = 'Unable to retrieve your location.';
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Location access denied by user.';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Location information is unavailable.';
              break;
            case error.TIMEOUT:
              errorMessage = 'Location request timed out.';
              break;
          }
          reject(new Error(errorMessage));
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000 // 5 minutes
        }
      );
    });
  }
}

export const weatherService = new WeatherService();